/**
 * One activity, as it appears on the grid or in a list.
 *
 * The colour comes from the activity TYPE, via a palette slot — no code here or
 * anywhere else names a type, because which types exist is deployment data
 * (product-neutrality rule, CLAUDE.md §1). A deployment that renames its
 * install type keeps its colour; one that adds a ninth type wraps around.
 *
 * The three badges are the whole reason this is a component rather than a div:
 * a synced activity, one that lost a conflict, and one whose contact was
 * deleted all need to say so at a glance, and all three are easy to forget.
 */
import { AlertTriangle, CalendarCheck, UserX } from 'lucide-react';
import React from 'react';
import { ACTIVITY_STATUS_LABELS, formatTime } from '../../lib/format.js';
import { S } from '../../lib/strings.js';

/** Finished or abandoned activities recede; the ones still owed stand out. */
const STATUS_CLASS = {
  DONE: 'opacity-60',
  CANCELLED: 'opacity-50 line-through',
  MISSED: 'ring-1 ring-danger',
  SCHEDULED: '',
};

export function ActivityChip({ activity, color, compact = false, onClick, onMouseDown }) {
  const conflict = activity.sync?.hasConflict;
  const synced = Boolean(activity.sync?.googleEventId);

  const title = [
    activity.title,
    activity.contact?.displayName,
    ACTIVITY_STATUS_LABELS[activity.status],
    conflict ? S.calendar.conflictHint : null,
    activity.orphaned ? S.calendar.orphanedHint : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={onMouseDown}
      title={title}
      className={[
        'flex h-full w-full flex-col overflow-hidden rounded border-s-4 bg-surface px-2 py-1',
        'text-start text-xs leading-tight shadow-sm hover:bg-surface-hover',
        STATUS_CLASS[activity.status] ?? '',
      ].join(' ')}
      // The only inline style: the palette slot for this activity's type. It is
      // a token reference, not a literal.
      style={{ borderInlineStartColor: color }}
    >
      <span className="flex items-center gap-1 truncate font-medium text-text">
        {!compact && !activity.allDay && (
          <span className="text-text-muted" dir="ltr">
            {formatTime(activity.startsAt)}
          </span>
        )}
        <span className="truncate">{activity.title}</span>
      </span>

      {activity.contact?.displayName && (
        <span className="truncate text-text-muted">{activity.contact.displayName}</span>
      )}

      {(conflict || activity.orphaned || synced) && (
        <span className="mt-auto flex items-center gap-1 pt-1">
          {conflict && (
            <AlertTriangle
              size={11}
              className="text-warning"
              aria-label={S.calendar.conflict}
            />
          )}
          {activity.orphaned && (
            <UserX size={11} className="text-danger" aria-label={S.calendar.orphaned} />
          )}
          {synced && !conflict && (
            <CalendarCheck
              size={11}
              className="text-text-muted"
              aria-label={S.calendar.syncedWithGoogle}
            />
          )}
        </span>
      )}
    </button>
  );
}
